import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  ChevronLeft, Loader2, Upload, Plus, Trash2, FileText, 
  User, Building2, Mail, Phone, DollarSign, Clock, Send,
  FileSpreadsheet, Eye
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const AdminCreateQuote = () => {
  const navigate = useNavigate();
  
  // Customer state
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  
  // Customer details
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyAbn, setCompanyAbn] = useState('');
  
  // Quote notes
  const [notes, setNotes] = useState('');
  const [sendImmediately, setSendImmediately] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Items
  const [items, setItems] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Pricing data
  const [technologies, setTechnologies] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState('');
  const [designRate, setDesignRate] = useState(85);
  
  // Submission
  const [submitting, setSubmitting] = useState(false);
  
  // Load initial data
  useEffect(() => {
    loadPricingData();
    loadDesignRate();
    searchCustomers('');
  }, []);
  
  const loadPricingData = async () => {
    try {
      const [techRes, matRes, delRes] = await Promise.all([
        axios.get(`${API}/settings/technologies`),
        axios.get(`${API}/settings/materials`),
        axios.get(`${API}/settings/delivery-options`)
      ]);
      setTechnologies(techRes.data.technologies || []);
      setMaterials(matRes.data.materials || []);
      setDeliveryOptions(delRes.data.delivery_options || []);
      
      // Set default delivery option
      if (delRes.data.delivery_options?.length > 0) {
        setSelectedDelivery(delRes.data.delivery_options[0].delivery_id);
      }
    } catch (error) {
      console.error('Failed to load pricing data:', error);
    }
  };
  
  const loadDesignRate = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/design-rate`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setDesignRate(response.data.hourly_rate || 85);
    } catch (error) {
      console.error('Failed to load design rate:', error);
    }
  };
  
  const searchCustomers = async (query) => {
    try {
      const response = await axios.get(`${API}/admin/customers/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Failed to search customers:', error);
    }
  };
  
  // Debounced customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch.length >= 2) {
        searchCustomers(customerSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);
  
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name || '');
    setCustomerEmail(customer.email || '');
    setCustomerPhone(customer.phone || '');
    setCompanyName(customer.company || '');
    setIsNewCustomer(false);
    setCustomerOpen(false);
  };
  
  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setCompanyName('');
    setCompanyAbn('');
    setIsNewCustomer(true);
  };
  
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setUploadingFile(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/quote/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const fileType = response.data.file_type?.toLowerCase();
        const needsManualPrice = fileType === 'step' || fileType === 'stp' || 
                                 fileType === 'iges' || fileType === 'igs' ||
                                 response.data.volume_needs_manual_entry;
        
        // Get default material and calculate initial price
        const defaultMaterialId = materials.length > 0 ? materials[0].material_id : '';
        const volume = response.data.volume_cm3 || 0;
        let initialPrice = '';
        
        // Always calculate price if we have volume and material (even for STEP files)
        if (volume > 0 && defaultMaterialId) {
          const material = materials.find(m => m.material_id === defaultMaterialId);
          if (material) {
            initialPrice = (volume * material.price_per_cm3).toFixed(2);
          }
        }
        
        setItems(prev => [...prev, {
          file_id: response.data.file_id,
          file_name: response.data.filename,
          file_type: fileType,
          volume_cm3: volume,
          needs_manual_price: needsManualPrice,
          unit_price: initialPrice,  // Pre-populated price (admin can override)
          material_id: defaultMaterialId,
          technology_id: technologies.length > 0 ? technologies[0].tech_id : '',
          quantity: 1,
          design_hours: 0
        }]);
        
        toast.success(`Uploaded: ${file.name}`);
      } catch (error) {
        const detail = error.response?.data?.detail || 'Upload failed';
        toast.error(`${file.name}: ${detail}`);
      }
    }
    
    setUploadingFile(false);
    e.target.value = '';
  };
  
  const updateItem = (index, field, value) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      
      const updated = { ...item, [field]: value };
      
      // Auto-recalculate unit_price when material changes (for all files with volume)
      if (field === 'material_id' && item.volume_cm3 > 0) {
        const material = materials.find(m => m.material_id === value);
        if (material) {
          updated.unit_price = (item.volume_cm3 * material.price_per_cm3).toFixed(2);
        }
      }
      
      return updated;
    }));
  };
  
  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };
  
  const getMaterialPrice = (materialId) => {
    const mat = materials.find(m => m.material_id === materialId);
    return mat?.price_per_cm3 || 0;
  };
  
  const getMaterialName = (materialId) => {
    const mat = materials.find(m => m.material_id === materialId);
    return mat?.name || '';
  };
  
  const calculateItemPrice = (item) => {
    const unitPrice = parseFloat(item.unit_price) || 0;
    return unitPrice * (item.quantity || 1);
  };
  
  const calculateItemDesignCost = (item) => {
    return (parseFloat(item.design_hours) || 0) * designRate;
  };
  
  const calculateTotals = () => {
    let partsSubtotal = 0;
    let totalDesign = 0;
    
    items.forEach(item => {
      partsSubtotal += calculateItemPrice(item);
      totalDesign += calculateItemDesignCost(item);
    });
    
    const deliveryOpt = deliveryOptions.find(d => d.delivery_id === selectedDelivery);
    const deliveryCost = deliveryOpt?.price || 0;
    
    const subtotalBeforeGst = partsSubtotal + totalDesign + deliveryCost;
    const gst = subtotalBeforeGst * 0.10;
    const total = subtotalBeforeGst + gst;
    
    return {
      partsSubtotal: partsSubtotal.toFixed(2),
      totalDesign: totalDesign.toFixed(2),
      deliveryCost: deliveryCost.toFixed(2),
      gst: gst.toFixed(2),
      total: total.toFixed(2)
    };
  };
  
  const handleSubmit = async () => {
    // Validation
    if (!customerEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (items.length === 0) {
      toast.error('At least one item is required');
      return;
    }
    
    // Validate all items have prices
    for (const item of items) {
      if (!item.unit_price || parseFloat(item.unit_price) <= 0) {
        toast.error(`Please enter a price for ${item.file_name}`);
        return;
      }
    }
    
    setSubmitting(true);
    
    try {
      const payload = {
        customer_id: selectedCustomer?.user_id || null,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        company_name: companyName,
        company_abn: companyAbn,
        items: items.map(item => ({
          file_id: item.file_id,
          file_name: item.file_name,
          volume_cm3: item.volume_cm3,
          material_id: item.material_id,
          quantity: parseInt(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          design_hours: parseFloat(item.design_hours) || 0
        })),
        delivery_option_id: selectedDelivery,
        notes: notes,
        send_immediately: sendImmediately
      };
      
      const response = await axios.post(`${API}/admin/quote-requests/create-manual`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      toast.success(response.data.message || 'Quote request created!');
      navigate('/admin/quote-requests');
      
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to create quote';
      toast.error(detail);
    }
    
    setSubmitting(false);
  };
  
  const totals = calculateTotals();

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link to="/admin/quote-requests">
              <Button variant="outline" size="icon" className="border-white/20 text-white hover:bg-white/5 rounded-none">
                <ChevronLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <span className="overline text-[#CCFF00]">Admin</span>
              <h1 className="text-2xl font-['Chivo'] font-black text-white">Create Quote</h1>
              <p className="text-white/50 text-sm">For walk-in customers or phone/email enquiries</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Customer Selection */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-white font-bold mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#CCFF00]" />
                  Customer Details
                </h2>
                
                <div className="space-y-4">
                  {/* Customer Type Toggle */}
                  <div className="flex gap-2 mb-4">
                    <Button
                      variant={isNewCustomer ? "outline" : "default"}
                      className={!isNewCustomer 
                        ? "bg-[#CCFF00] text-black hover:bg-[#B3E600] flex-1" 
                        : "border-white/20 text-white hover:bg-white/5 flex-1"
                      }
                      onClick={() => setIsNewCustomer(false)}
                    >
                      <User className="w-4 h-4 mr-2" />
                      Existing Customer
                    </Button>
                    <Button
                      variant={isNewCustomer ? "default" : "outline"}
                      className={isNewCustomer 
                        ? "bg-[#CCFF00] text-black hover:bg-[#B3E600] flex-1" 
                        : "border-white/20 text-white hover:bg-white/5 flex-1"
                      }
                      onClick={() => { setIsNewCustomer(true); setSelectedCustomer(null); }}
                      data-testid="new-customer-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Customer
                    </Button>
                  </div>

                  {/* Customer Search - Only show when NOT new customer */}
                  {!isNewCustomer && (
                    <div>
                      <Label className="text-white/70">Search Existing Customer</Label>
                      <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal bg-white/5 border-white/10 text-white mt-1"
                            data-testid="customer-search-btn"
                          >
                            {selectedCustomer ? (
                              <span>{selectedCustomer.name} ({selectedCustomer.email})</span>
                            ) : (
                              <span className="text-white/50">Click to search by name, email, or company...</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0 bg-[#111] border-white/10" align="start">
                          <Command className="bg-transparent" shouldFilter={false}>
                            <CommandInput 
                              placeholder="Type to search customers..." 
                              value={customerSearch}
                              onValueChange={setCustomerSearch}
                              className="border-none focus:ring-0"
                            />
                            <CommandList>
                              {customers.length === 0 ? (
                                <div className="py-6 text-center text-white/50">
                                  No customers found.
                                  <Button 
                                    variant="link" 
                                    className="text-[#CCFF00] block mx-auto mt-2"
                                    onClick={() => { setIsNewCustomer(true); setSelectedCustomer(null); setCustomerOpen(false); }}
                                  >
                                    + Add New Customer Instead
                                  </Button>
                                </div>
                              ) : (
                                <CommandGroup>
                                  {customers.map((customer) => (
                                    <CommandItem
                                      key={customer.user_id || customer.email}
                                      value={customer.email}
                                      onSelect={() => selectCustomer(customer)}
                                      className="cursor-pointer hover:bg-white/5"
                                    >
                                      <div>
                                        <p className="text-white font-medium">{customer.name}</p>
                                        <p className="text-white/50 text-sm">{customer.email}</p>
                                        {customer.company && (
                                          <p className="text-[#CCFF00]/70 text-xs">{customer.company}</p>
                                        )}
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              )}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      
                      {selectedCustomer && (
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="text-white/50 mt-1 p-0 h-auto"
                          onClick={clearCustomer}
                        >
                          Clear selection
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Customer Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70">Name *</Label>
                      <Input
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Customer name"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70">Email *</Label>
                      <Input
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholder="customer@example.com"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70">Phone</Label>
                      <Input
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        placeholder="+61 400 000 000"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70">Company</Label>
                      <Input
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Company name"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-white/70">ABN</Label>
                      <Input
                        value={companyAbn}
                        onChange={(e) => setCompanyAbn(e.target.value)}
                        placeholder="12 345 678 901"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Parts Upload */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-white font-bold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#CCFF00]" />
                  Parts
                </h2>
                
                {/* Upload Area */}
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-sm cursor-pointer hover:border-[#CCFF00]/50 transition-colors mb-4">
                  <div className="flex flex-col items-center">
                    {uploadingFile ? (
                      <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-white/50 mb-2" />
                        <p className="text-white/50 text-sm">Drop files or click to upload</p>
                        <p className="text-white/30 text-xs mt-1">STL, STEP, OBJ, 3MF, IGES</p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept=".stl,.obj,.3mf,.step,.stp,.iges,.igs"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                  />
                </label>
                
                {/* Items List */}
                {items.length === 0 ? (
                  <p className="text-white/30 text-center py-4">No parts added yet</p>
                ) : (
                  <div className="space-y-4">
                    {items.map((item, index) => (
                      <div key={item.file_id} className="bg-white/5 border border-white/10 rounded-sm p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="text-white font-medium">{item.file_name}</p>
                            <p className="text-white/50 text-xs">
                              {item.file_type?.toUpperCase()} 
                              {item.volume_cm3 > 0 && ` • ${item.volume_cm3.toFixed(2)} cm³`}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(index)}
                            className="text-red-400 hover:text-red-400 hover:bg-red-400/10 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {/* Material Selection */}
                          <div>
                            <Label className="text-white/50 text-xs">Material</Label>
                            <Select
                              value={item.material_id}
                              onValueChange={(v) => updateItem(index, 'material_id', v)}
                            >
                              <SelectTrigger className="bg-white/5 border-white/10 text-white h-9 mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#111] border-white/10">
                                {materials.map(m => (
                                  <SelectItem key={m.material_id} value={m.material_id} className="text-white">
                                    {m.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Quantity */}
                          <div>
                            <Label className="text-white/50 text-xs">Qty</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                              className="bg-white/5 border-white/10 text-white h-9 mt-1"
                            />
                          </div>
                          
                          {/* Price - always editable, pre-populated with calculated value */}
                          <div>
                            <Label className="text-white/50 text-xs">
                              Unit Price ($){item.needs_manual_price ? ' *' : ''}
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.unit_price ?? ''}
                              onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                              placeholder={item.needs_manual_price ? "Enter price" : "0.00"}
                              className="bg-white/5 border-white/10 text-white h-9 mt-1"
                            />
                            {!item.needs_manual_price && item.volume_cm3 > 0 && (
                              <p className="text-white/30 text-xs mt-1">
                                Auto: ${(item.volume_cm3 * getMaterialPrice(item.material_id)).toFixed(2)}
                              </p>
                            )}
                          </div>
                          
                          {/* Design Hours */}
                          <div>
                            <Label className="text-white/50 text-xs">Design Hrs</Label>
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              value={item.design_hours}
                              onChange={(e) => updateItem(index, 'design_hours', e.target.value)}
                              placeholder="0"
                              className="bg-white/5 border-white/10 text-white h-9 mt-1"
                            />
                          </div>
                        </div>
                        
                        {/* Item Total */}
                        <div className="mt-3 pt-3 border-t border-white/10 flex justify-between text-sm">
                          <span className="text-white/50">
                            Part: ${calculateItemPrice(item).toFixed(2)}
                            {item.design_hours > 0 && (
                              <span className="ml-2">+ Design: ${calculateItemDesignCost(item).toFixed(2)}</span>
                            )}
                          </span>
                          <span className="text-[#CCFF00] font-bold">
                            ${(calculateItemPrice(item) + calculateItemDesignCost(item)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Notes */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-white font-bold mb-4">Notes</h2>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special instructions or notes..."
                  className="bg-white/5 border-white/10 text-white min-h-[100px]"
                />
              </div>
            </div>
            
            {/* Sidebar - Quote Settings & Summary */}
            <div className="space-y-6">
              {/* Quote Settings */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-white font-bold mb-4">Quote Settings</h2>
                
                <div className="space-y-4">
                  {/* Delivery Option */}
                  <div>
                    <Label className="text-white/70">Delivery</Label>
                    <Select value={selectedDelivery} onValueChange={setSelectedDelivery}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111] border-white/10">
                        {deliveryOptions.map(d => (
                          <SelectItem key={d.delivery_id} value={d.delivery_id} className="text-white">
                            {d.name} - ${d.price.toFixed(2)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Design Rate Info */}
                  <div className="pt-3 border-t border-white/10">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/50 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Design Rate
                      </span>
                      <span className="text-white">${designRate.toFixed(2)}/hr</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Order Summary */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-white font-bold mb-4">Summary</h2>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Parts ({items.length})</span>
                    <span className="text-white">${totals.partsSubtotal}</span>
                  </div>
                  {parseFloat(totals.totalDesign) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-white/50">Design</span>
                      <span className="text-white">${totals.totalDesign}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-white/50">Delivery</span>
                    <span className="text-white">${totals.deliveryCost}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span className="text-white/50">GST (10%)</span>
                    <span className="text-white">${totals.gst}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10 text-lg font-bold">
                    <span className="text-white">Total</span>
                    <span className="text-[#CCFF00]">${totals.total}</span>
                  </div>
                </div>
                
                {/* Send Immediately Option */}
                <div className="flex items-start gap-3 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg mt-4">
                  <Checkbox 
                    id="send-immediately"
                    checked={sendImmediately}
                    onCheckedChange={setSendImmediately}
                    className="mt-0.5 border-cyan-500 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                    data-testid="send-immediately-checkbox"
                  />
                  <div className="flex-1">
                    <Label 
                      htmlFor="send-immediately" 
                      className="text-white cursor-pointer font-medium"
                    >
                      Send quote to customer immediately
                    </Label>
                    <p className="text-white/50 text-xs mt-1">
                      Skip the review queue and email the quote directly with PDF attachment. 
                      You&apos;ll receive a CC copy.
                    </p>
                  </div>
                </div>
                
                {/* Preview Button */}
                {sendImmediately && (
                  <Button
                    variant="outline"
                    className="w-full mt-4 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                    onClick={() => setShowPreview(true)}
                    disabled={items.length === 0 || !customerEmail}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Email
                  </Button>
                )}
                
                {/* Submit Button */}
                <Button
                  className={`w-full ${sendImmediately ? 'mt-3' : 'mt-6'} bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold py-6`}
                  onClick={handleSubmit}
                  disabled={submitting || items.length === 0}
                  data-testid="submit-quote-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  {sendImmediately ? 'Create & Send Quote' : 'Create Quote Request'}
                </Button>
                
                <p className="text-white/30 text-xs text-center mt-3">
                  {sendImmediately 
                    ? 'Quote with PDF will be emailed to customer (CC to admin)'
                    : 'Quote will appear in your queue for review before sending'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Email Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-white text-black max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-black">Email Preview</DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg overflow-hidden">
            {/* Email Header */}
            <div className="bg-[#111] text-white p-6 text-center">
              <h1 className="text-[#CCFF00] text-2xl font-bold m-0">3D Print Bureau</h1>
              <p className="text-gray-400 mt-2">Your Quote is Ready!</p>
            </div>
            
            {/* Email Body */}
            <div className="p-6 bg-gray-50">
              <p className="text-base">Hi {customerName || 'Customer'},</p>
              <p className="mt-2">Thank you for your enquiry. We&apos;ve prepared a detailed quote for your 3D printing project.</p>
              
              {/* Notes */}
              {notes && (
                <div className="bg-white p-4 border-l-4 border-[#CCFF00] my-5">
                  <p className="m-0 text-gray-700">{notes}</p>
                </div>
              )}
              
              {/* Items Table */}
              <h3 className="text-gray-800 mt-6 mb-3">Quote Breakdown</h3>
              <table className="w-full border-collapse bg-white mb-3">
                <thead>
                  <tr className="bg-[#333] text-white">
                    <th className="p-2 text-left">Part Name</th>
                    <th className="p-2 text-right">Volume</th>
                    <th className="p-2 text-center">Qty</th>
                    <th className="p-2 text-left">Material</th>
                    <th className="p-2 text-right">Unit Price</th>
                    <th className="p-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const unitPrice = parseFloat(item.unit_price) || 0;
                    const lineTotal = unitPrice * (parseInt(item.quantity) || 1);
                    const material = materials.find(m => m.material_id === item.material_id);
                    
                    return (
                      <tr key={idx} className="border-b border-gray-200">
                        <td className="p-2">{item.file_name}</td>
                        <td className="p-2 text-right">{item.volume_cm3?.toFixed(2)} cm³</td>
                        <td className="p-2 text-center">{item.quantity || 1}</td>
                        <td className="p-2">{material?.name || 'N/A'}</td>
                        <td className="p-2 text-right">${unitPrice.toFixed(2)}</td>
                        <td className="p-2 text-right font-semibold">${lineTotal.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {/* Total Box */}
              <div className="bg-[#111] text-white p-5 text-center my-5 rounded">
                <p className="m-0 text-xs text-gray-400 uppercase tracking-wider">Total Quote</p>
                <p className="m-0 mt-2 text-3xl text-[#CCFF00] font-bold">${totals.total} AUD</p>
              </div>
              
              {/* CTA Button Preview */}
              <div className="text-center my-6">
                <div className="inline-block bg-[#CCFF00] text-black py-4 px-8 font-bold rounded">
                  ✓ APPROVE QUOTE & REQUEST INVOICE
                </div>
              </div>
              
              <div className="bg-gray-100 p-4 rounded text-center my-5">
                <p className="m-0 text-gray-600 text-sm">Click the button above to approve this quote and receive your invoice.</p>
                <p className="m-0 mt-2 text-gray-400 text-xs">A PDF copy of this quote is attached for your records.</p>
              </div>
              
              <p className="text-gray-600 text-center text-sm">Have questions? Simply reply to this email.</p>
              
              <p className="text-gray-600 mt-6">Best regards,<br/><strong>3D Print Bureau Team</strong></p>
            </div>
          </div>
          
          <div className="flex gap-3 mt-4">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setShowPreview(false)}
            >
              Close Preview
            </Button>
            <Button 
              className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600]"
              onClick={() => {
                setShowPreview(false);
                handleSubmit();
              }}
              disabled={submitting}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Quote
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default AdminCreateQuote;
